import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { invoiceAPI, quoteAPI, clientAPI, emailTemplateAPI, signatureAPI, settingsAPI, servicesAPI } from '../../services/api';
import { Button, FormField, Input, Textarea, Select, ConfirmDialog, Modal } from '../../components/common';
import { toast } from 'react-toastify';

const SendIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

/* ─── Génération PDF ─────────────────────────────────────── */
export const usePdf = () => {
  const [loading, setLoading] = useState(false);

  /* Ouvre le HTML dans une popup pour aperçu écran */
  const openPdf = async (fetchFn, filename) => {
    setLoading(true);
    const toastId = toast.loading('Préparation du document…');
    try {
      const response = await fetchFn();
      const htmlText = typeof response.data === 'string' ? response.data : '';
      if (!htmlText || htmlText.trimStart().startsWith('{')) {
        const j = JSON.parse(htmlText); throw new Error(j.message || 'Erreur serveur');
      }
      toast.dismiss(toastId);
      const popup = window.open('', '_blank', 'width=900,height=1200,scrollbars=yes');
      if (!popup) { toast.error('Popups bloquées — autorisez-les pour ce site'); return; }
      popup.document.open();
      popup.document.write(htmlText);
      popup.document.close();
      popup.focus();
      toast.success('Document ouvert dans un nouvel onglet ✓');
    } catch (err) {
      console.error('[usePdf:open]', err);
      toast.dismiss();
      toast.error('Erreur PDF : ' + (err.message || 'Erreur inconnue'));
    } finally { setLoading(false); }
  };

  /**
   * Télécharge le PDF directement (sans fenêtre d'impression).
   * @param {Function} downloadBinaryFn  - Appel API qui retourne un Blob (/pdf-download)
   * @param {string}   filename          - Nom du fichier (sans .pdf)
   */
  const downloadPdf = async (downloadBinaryFn, filename) => {
    setLoading(true);
    const toastId = toast.loading('Génération du PDF…');
    try {
      // 1. Tentative téléchargement binaire côté serveur
      const resp = await downloadBinaryFn();
      const contentType = resp.headers?.['content-type'] || '';

      if (contentType.includes('application/pdf')) {
        // ✅ Le serveur a généré un vrai PDF — téléchargement direct
        toast.dismiss(toastId);
        const blob = new Blob([resp.data], { type: 'application/pdf' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('PDF téléchargé ✓');
        return;
      }

      // ❌ Le serveur ne peut pas générer de PDF binaire
      toast.dismiss(toastId);
      toast.error('Erreur lors de la génération PDF. Utilisez "Aperçu PDF" (📄) pour visualiser et imprimer.', { autoClose: 7000 });

    } catch (err) {
      console.error('[usePdf:download]', err);
      toast.dismiss();
      toast.error('Erreur PDF : ' + (err.message || 'Erreur inconnue'));
    } finally { setLoading(false); }
  };

  return { loading, openPdf, downloadPdf };
};
/* ─── Éditeur de lignes ─── */
const LinesEditor = ({ items, onChange, discount_pct, tax_rate, onDiscountChange, onTaxChange }) => {
  const [services,     setServices]     = React.useState([]);
  const [showServices, setShowServices] = React.useState(false);
  const [svcFilter,    setSvcFilter]    = React.useState('');

  React.useEffect(() => {
    servicesAPI.list().then(({ data }) => setServices(data.data || [])).catch(() => {});
  }, []);

  const addLine    = () => onChange([...items, { description: '', quantity: 1, unit_price: 0, discount: 0 }]);
  const removeLine = (i) => onChange(items.filter((_, idx) => idx !== i));
  const upd        = (i, k, v) => onChange(items.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const insertService = (svc) => {
    onChange([...items, { description: svc.name + (svc.description ? ` — ${svc.description}` : ''), quantity: 1, unit_price: parseFloat(svc.unit_price || 0), discount: 0 }]);
    setShowServices(false); setSvcFilter('');
  };

  const filteredSvc = services.filter(s => !svcFilter || s.name.toLowerCase().includes(svcFilter.toLowerCase()) || (s.category||'').toLowerCase().includes(svcFilter.toLowerCase()));

  const sub  = items.reduce((s, l) => s + (parseFloat(l.quantity)||0) * (parseFloat(l.unit_price)||0) * (1-(parseFloat(l.discount)||0)/100), 0);
  const disc = sub * (parseFloat(discount_pct)||0) / 100;
  const tax  = (sub - disc) * (parseFloat(tax_rate)||0) / 100;
  const total = sub - disc + tax;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={{ fontWeight:700, fontSize:14 }}>Lignes de prestation</div>
        <div style={{ display:'flex', gap:8 }}>
          {services.length > 0 && (
            <div style={{ position:'relative' }}>
              <Button size="sm" variant="ghost" onClick={() => setShowServices(v => !v)}>📋 Prestations</Button>
              {showServices && (
                <div onMouseLeave={() => setShowServices(false)}
                  style={{ position:'absolute', top:'100%', right:0, zIndex:200, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', boxShadow:'0 4px 20px rgba(0,0,0,.1)', minWidth:300, maxHeight:260, overflowY:'auto', marginTop:4 }}>
                  <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border)' }}>
                    <input className="form-input" value={svcFilter} onChange={e => setSvcFilter(e.target.value)} placeholder="Rechercher une prestation…" style={{ fontSize:12 }} autoFocus />
                  </div>
                  {filteredSvc.map(s => (
                    <div key={s.id} onClick={() => insertService(s)}
                      style={{ padding:'8px 12px', cursor:'pointer', borderBottom:'1px solid var(--border)', fontSize:13 }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--bg-page)'}
                      onMouseLeave={e => e.currentTarget.style.background=''}>
                      <div style={{ fontWeight:600 }}>{s.name}</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)', display:'flex', justifyContent:'space-between' }}>
                        <span>{s.category || ''}</span>
                        <span style={{ color:'var(--brand-accent)', fontWeight:700 }}>{parseFloat(s.unit_price||0).toFixed(2)} € / {s.unit}</span>
                      </div>
                    </div>
                  ))}
                  {filteredSvc.length === 0 && <div style={{ padding:12, fontSize:13, color:'var(--text-muted)' }}>Aucune prestation trouvée</div>}
                </div>
              )}
            </div>
          )}
          <Button size="sm" variant="secondary" onClick={addLine}>+ Ajouter une ligne</Button>
        </div>
      </div>

      <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:16 }}>
        <thead>
          <tr style={{ background:'var(--bg-page)' }}>
            {['Description', 'Qté', 'P.U. HT (€)', 'Remise (%)', 'Total HT', ''].map(h => (
              <th key={h} style={{ padding:'7px 8px', textAlign:'left', fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', borderBottom:'2px solid var(--border)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((line, i) => {
            const lt = (parseFloat(line.quantity)||0)*(parseFloat(line.unit_price)||0)*(1-(parseFloat(line.discount)||0)/100);
            return (
              <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                <td style={{ padding:'5px 4px' }}>
                  <input className="form-input" value={line.description} onChange={e=>upd(i,'description',e.target.value)} placeholder="Description du service…" style={{ fontSize:13, width:'100%' }}/>
                </td>
                <td style={{ padding:'5px 4px', width:65 }}>
                  <input className="form-input" type="number" min="1" value={line.quantity} onChange={e=>upd(i,'quantity',e.target.value)} style={{ fontSize:13, textAlign:'center' }}/>
                </td>
                <td style={{ padding:'5px 4px', width:110 }}>
                  <input className="form-input" type="number" step="0.01" min="0" value={line.unit_price} onChange={e=>upd(i,'unit_price',e.target.value)} style={{ fontSize:13, textAlign:'right' }}/>
                </td>
                <td style={{ padding:'5px 4px', width:70 }}>
                  <input className="form-input" type="number" min="0" max="100" value={line.discount} onChange={e=>upd(i,'discount',e.target.value)} style={{ fontSize:13, textAlign:'center' }}/>
                </td>
                <td style={{ padding:'5px 8px', fontWeight:600, fontSize:13, whiteSpace:'nowrap', minWidth:90 }}>
                  {lt.toFixed(2)} €
                </td>
                <td style={{ padding:'5px 4px', width:30, textAlign:'center' }}>
                  {items.length > 1 && (
                    <button onClick={()=>removeLine(i)} style={{ color:'var(--error)', fontSize:20, cursor:'pointer', background:'none', border:'none', lineHeight:1 }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Totaux */}
      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <div style={{ width:290, background:'var(--bg-page)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', overflow:'hidden' }}>
          {[
            ['Sous-total HT', sub.toFixed(2) + ' €', false],
          ].map(([l,v]) => (
            <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'8px 14px', borderBottom:'1px solid var(--border)', fontSize:13 }}>
              <span>{l}</span><strong>{v}</strong>
            </div>
          ))}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 14px', borderBottom:'1px solid var(--border)', fontSize:13, gap:8 }}>
            <span>Remise globale (%)</span>
            <input type="number" min="0" max="100" value={discount_pct} onChange={e=>onDiscountChange(e.target.value)}
              style={{ width:55, padding:'3px 6px', border:'1px solid var(--border)', borderRadius:4, fontSize:12, textAlign:'center' }}/>
          </div>
          {disc > 0 && (
            <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 14px', fontSize:12, color:'var(--success)', borderBottom:'1px solid var(--border)' }}>
              <span>Remise</span><span>- {disc.toFixed(2)} €</span>
            </div>
          )}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 14px', borderBottom:'1px solid var(--border)', fontSize:13, gap:8 }}>
            <span>TVA (%)</span>
            <input type="number" min="0" max="100" value={tax_rate} onChange={e=>onTaxChange(e.target.value)}
              style={{ width:55, padding:'3px 6px', border:'1px solid var(--border)', borderRadius:4, fontSize:12, textAlign:'center' }}/>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 14px', borderBottom:'1px solid var(--border)', fontSize:13 }}>
            <span>TVA</span><strong>{tax.toFixed(2)} €</strong>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', padding:'12px 14px', fontSize:16, fontWeight:800, background:'var(--brand-accent)', color:'white' }}>
            <span>Total TTC</span><span>{total.toFixed(2)} €</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Page d'édition partagée (facture ou devis) ─── */
const DocumentEditPage = ({ type, forceNew = false }) => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const isNew      = forceNew || !id || id === 'new' || id === 'undefined';
  const isInvoice  = type === 'invoice';
  const apiObj     = isInvoice ? invoiceAPI : quoteAPI;
  const backUrl    = isInvoice ? '/admin/invoices' : '/admin/quotes';
  const label      = isInvoice ? 'Facture' : 'Devis';

  const [loading,        setLoading]        = useState(!isNew);
  const [saving,         setSaving]         = useState(false);
  const [clients,        setClients]        = useState([]);
  const [doc,            setDoc]            = useState(null);
  const [convertConfirm, setConvertConfirm] = useState(false);
  const { loading: pdfLoading, openPdf, downloadPdf } = usePdf();

  const downloadXml = async () => {
    try {
      const resp = await invoiceAPI.downloadXml(id);
      const blob = new Blob([resp.data], { type: 'application/xml' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `facturx-${doc?.reference || id}.xml`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success('XML Factur-X téléchargé ✓');
    } catch { toast.error('Erreur génération XML'); }
  };

  const emptyForm = {
    client_id: '', client_snapshot: {},
    items: [{ description: '', quantity: 1, unit_price: 0, discount: 0 }],
    discount_pct: 0, tax_rate: 20, notes: '',
    status: isInvoice ? 'draft' : 'pending',
    due_date:    isInvoice ? new Date(Date.now()+30*86400000).toISOString().split('T')[0] : '',
    valid_until: !isInvoice ? new Date(Date.now()+30*86400000).toISOString().split('T')[0] : '',
  };
  const [form, setForm] = useState(emptyForm);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Chargement
  useEffect(() => {
    // Ne plus charger tous les clients d'un coup — recherche dynamique à la frappe
    setClients([]);
    if (!isNew) {
      setLoading(true);
      apiObj.get(id)
        .then(({ data }) => {
          const d = data.data;
          setDoc(d);
          const items = Array.isArray(d.items) ? d.items : JSON.parse(d.items || '[]');
          const snap  = typeof d.client_snapshot === 'object' ? d.client_snapshot : JSON.parse(d.client_snapshot || '{}');
          setForm({
            client_id:       d.client_id || '',
            client_snapshot: snap,
            items:           items.length ? items : [{ description:'', quantity:1, unit_price:0, discount:0 }],
            discount_pct:    d.discount_pct || 0,
            tax_rate:        d.tax_rate || 20,
            notes:           d.notes || '',
            status:          d.status,
            due_date:        d.due_date?.split('T')[0] || '',
            valid_until:     d.valid_until?.split('T')[0] || '',
          });
        })
        .catch(() => { toast.error('Document introuvable'); navigate(backUrl); })
        .finally(() => setLoading(false));
    }
  }, [id]); // eslint-disable-line

  // Factures : verrouillée si règlements existent. Devis : toujours modifiable sauf le statut.
  const hasPayments = isInvoice && doc && (doc.payments?.length ?? 0) > 0;
  // canEdit = les champs (lignes, client, notes, etc.) sont modifiables ?
  // Devis : envoyé+ → champs verrouillés, seul le statut reste modifiable
  // Factures : verrouillée dès qu'un règlement existe
  const canEditFields = isNew || !doc || (isInvoice
    ? !hasPayments
    : ['draft', 'pending'].includes(doc.status));
  // canEditStatus = le statut est modifiable ?
  // Devis : toujours. Factures : oui si aucun règlement.
  const canEditStatus = isNew || !doc || (isInvoice ? !hasPayments : true);

  const [clientSearch,   setClientSearch]   = useState('');
  const [showClientList, setShowClientList] = useState(false);
  const [searchLoading,  setSearchLoading]  = useState(false);
  const clientSearchTimer = React.useRef(null);

  const searchClients = (q) => {
    setClientSearch(q);
    if (!q.trim()) { setClients([]); setShowClientList(false); return; }
    setShowClientList(true);
    clearTimeout(clientSearchTimer.current);
    clientSearchTimer.current = setTimeout(() => {
      setSearchLoading(true);
      clientAPI.list({ search: q, per_page: 20 })
        .then(({ data }) => setClients(data.data || []))
        .catch(() => setClients([]))
        .finally(() => setSearchLoading(false));
    }, 280);
  };

  const selectClient = (c) => {
    f('client_id', c.id);
    f('client_snapshot', {
      company: c.company, first_name: c.first_name, last_name: c.last_name,
      email: c.email, phone: c.phone, address: c.address, city: c.city, zip_code: c.zip_code,
    });
    setClientSearch([c.company, [c.first_name, c.last_name].filter(Boolean).join(' ')].filter(Boolean).join(' — ') || c.email);
    setShowClientList(false);
    setClients([]);
  };

  const clearClient = () => {
    f('client_id', '');
    f('client_snapshot', {});
    setClientSearch('');
    setClients([]);
    setShowClientList(false);
  };

  const computeTotals = () => {
    const sub  = form.items.reduce((s,l) => s+(parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0)*(1-(parseFloat(l.discount)||0)/100), 0);
    const disc = sub*(parseFloat(form.discount_pct)||0)/100;
    const tax  = (sub-disc)*(parseFloat(form.tax_rate)||0)/100;
    return { subtotal_ht: sub-disc, discount_amount: disc, tax_amount: tax, total_ttc: sub-disc+tax };
  };

  const save = async () => {
    if (!form.items.length) { toast.error('Au moins une ligne requise'); return; }
    setSaving(true);
    try {
      const payload = { ...form, ...computeTotals() };
      if (isNew) await apiObj.create(payload);
      else       await apiObj.update(id, payload);
      toast.success(label + (isNew ? ' créé ✓' : ' mis à jour ✓'));
      navigate(backUrl);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const handleOpenPdf  = () => openPdf(() => apiObj.fetchPdf(id), label.toLowerCase() + '-' + (doc?.reference || id));
  const handleDownPdf  = () => downloadPdf(() => apiObj.downloadPdf(id), label.toLowerCase() + '-' + (doc?.reference || id));

  if (loading) return <div className="page-loader"><div className="spinner spinner-lg"/></div>;

  const statusLabels = isInvoice
    ? { draft:'Brouillon', sent:'Envoyé', paid:'Payé', partial:'Partiel', overdue:'En retard', cancelled:'Annulé' }
    : { pending:'En attente', sent:'Envoyé', accepted:'Accepté', rejected:'Refusé', expired:'Expiré', converted:'Converti', draft:'Brouillon' };

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1, overflowY:'auto' }}>
      {/* En-tête */}
      <div className="admin-page-header" style={{ flexShrink:0 }}>
        <div>
          <button onClick={() => navigate(backUrl)}
            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:13, marginBottom:6, padding:0 }}>
            ← Retour aux {label.toLowerCase()}s
          </button>
          <h1 className="admin-page-title">
            {isNew ? `Nouveau ${label.toLowerCase()}` : `${label} — ${doc?.reference || ''}`}
          </h1>
          {!canEditFields && (
            <div style={{ marginTop:6, padding:'6px 12px', background:'rgba(234,179,8,.1)', border:'1px solid rgba(234,179,8,.3)', borderRadius:'var(--radius-sm)', fontSize:13, color:'#92400e', display:'inline-flex', alignItems:'center', gap:6 }}>
              {isInvoice
                ? `🔒 Non modifiable — ${doc?.payments?.length} règlement(s) enregistré(s)`
                : `🔒 Champs verrouillés — le statut reste modifiable`}
            </div>
          )}
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {!isNew && (
            <>
              <Button variant="secondary" loading={pdfLoading} onClick={handleOpenPdf}>Aperçu PDF</Button>
              <Button variant="secondary" loading={pdfLoading} onClick={handleDownPdf}>Télécharger PDF</Button>
              {isInvoice && (
                <Button variant="ghost" onClick={downloadXml} title="Télécharger le XML Factur-X seul (pour import comptable)">
                  XML Factur-X
                </Button>
              )}
            </>
          )}
          {(canEditFields || canEditStatus) && <Button variant="primary" loading={saving} onClick={save}>Enregistrer</Button>}
        </div>
      </div>

      {/* Corps */}
      <div className="admin-page-content" style={{ flex:1 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:'var(--space-lg)', alignItems:'start' }}>

          {/* Colonne principale */}
          <div style={{ display:'flex', flexDirection:'column', gap:'var(--space-lg)' }}>

            {/* Sélection client */}
            <div className="admin-card" style={{ padding:'var(--space-lg)' }}>
              <h3 style={{ fontSize:14, fontWeight:700, marginBottom:'var(--space-md)', color:'var(--text-secondary)' }}>CLIENT</h3>
              <FormField label="Client enregistré">
                <div style={{ position:'relative' }}>
                  <div style={{ display:'flex', gap:6 }}>
                    <input
                      className="form-input"
                      value={clientSearch}
                      onChange={e => searchClients(e.target.value)}
                      onBlur={() => setTimeout(() => setShowClientList(false), 180)}
                      placeholder="Tapez un nom, entreprise ou email…"
                      disabled={!canEditFields}
                      style={{ flex:1 }}
                    />
                    {form.client_id && canEditFields && (
                      <button onClick={clearClient} style={{ background:'none', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'0 10px', cursor:'pointer', color:'var(--text-muted)', fontSize:16 }} title="Saisie manuelle">×</button>
                    )}
                  </div>
                  {showClientList && canEditFields && (
                    <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:100, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', boxShadow:'0 4px 16px rgba(0,0,0,.1)', maxHeight:260, overflowY:'auto', marginTop:2 }}>
                      {searchLoading && (
                        <div style={{ padding:'10px 12px', fontSize:13, color:'var(--text-muted)', textAlign:'center' }}>Recherche…</div>
                      )}
                      {!searchLoading && clients.length === 0 && (
                        <div style={{ padding:'10px 12px', fontSize:13, color:'var(--text-muted)' }}>Aucun client trouvé pour « {clientSearch} »</div>
                      )}
                      {!searchLoading && clients.map(c => (
                        <div
                          key={c.id}
                          onMouseDown={() => selectClient(c)}
                          style={{ padding:'9px 12px', fontSize:13, cursor:'pointer', borderBottom:'1px solid var(--border)', background: parseInt(form.client_id) === c.id ? 'rgba(230,57,70,.06)' : 'transparent' }}
                        >
                          <div style={{ fontWeight:600, color:'var(--text-primary)' }}>{c.company || [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email}</div>
                          {c.company && (c.first_name || c.last_name) && <div style={{ fontSize:12, color:'var(--text-muted)' }}>{[c.first_name, c.last_name].filter(Boolean).join(' ')}</div>}
                          {c.email && <div style={{ fontSize:12, color:'var(--text-muted)' }}>{c.email}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {form.client_id && <div style={{ fontSize:12, color:'var(--success)', marginTop:4 }}>✓ Client sélectionné — les champs ci-dessous sont pré-remplis</div>}
                {!form.client_id && <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>Aucun client sélectionné — remplissez les champs manuellement</div>}
              </FormField>
              {!form.client_id && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'var(--space-sm)', marginTop:'var(--space-md)' }}>
                  <FormField label="Entreprise">
                    <Input value={form.client_snapshot?.company||''} disabled={!canEditFields} onChange={e=>f('client_snapshot',{...form.client_snapshot,company:e.target.value})} placeholder="Mon Entreprise SAS"/>
                  </FormField>
                  <FormField label="Prénom">
                    <Input value={form.client_snapshot?.first_name||''} disabled={!canEditFields} onChange={e=>f('client_snapshot',{...form.client_snapshot,first_name:e.target.value})}/>
                  </FormField>
                  <FormField label="Nom">
                    <Input value={form.client_snapshot?.last_name||''} disabled={!canEditFields} onChange={e=>f('client_snapshot',{...form.client_snapshot,last_name:e.target.value})}/>
                  </FormField>
                  <FormField label="Email">
                    <Input type="email" value={form.client_snapshot?.email||''} disabled={!canEditFields} onChange={e=>f('client_snapshot',{...form.client_snapshot,email:e.target.value})} placeholder="contact@exemple.fr"/>
                  </FormField>
                  <FormField label="Téléphone">
                    <Input value={form.client_snapshot?.phone||''} disabled={!canEditFields} onChange={e=>f('client_snapshot',{...form.client_snapshot,phone:e.target.value})}/>
                  </FormField>
                  <FormField label="Adresse">
                    <Input value={form.client_snapshot?.address||''} disabled={!canEditFields} onChange={e=>f('client_snapshot',{...form.client_snapshot,address:e.target.value})}/>
                  </FormField>
                  <FormField label="Ville">
                    <Input value={form.client_snapshot?.city||''} disabled={!canEditFields} onChange={e=>f('client_snapshot',{...form.client_snapshot,city:e.target.value})}/>
                  </FormField>
                  <FormField label="Code postal">
                    <Input value={form.client_snapshot?.zip_code||''} disabled={!canEditFields} onChange={e=>f('client_snapshot',{...form.client_snapshot,zip_code:e.target.value})}/>
                  </FormField>
                </div>
              )}
            </div>

            {/* Lignes */}
            <div className="admin-card" style={{ padding:'var(--space-lg)' }}>
              <h3 style={{ fontSize:14, fontWeight:700, marginBottom:'var(--space-md)', color:'var(--text-secondary)' }}>PRESTATIONS</h3>
              <div style={{ pointerEvents: canEditFields ? 'auto' : 'none', opacity: canEditFields ? 1 : .6 }}>
                <LinesEditor
                  items={form.items}
                  onChange={items => f('items', items)}
                  discount_pct={form.discount_pct}
                  tax_rate={form.tax_rate}
                  onDiscountChange={v => f('discount_pct', v)}
                  onTaxChange={v => f('tax_rate', v)}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="admin-card" style={{ padding:'var(--space-lg)' }}>
              <h3 style={{ fontSize:14, fontWeight:700, marginBottom:'var(--space-md)', color:'var(--text-secondary)' }}>NOTES ET CONDITIONS</h3>
              <Textarea rows={4} value={form.notes} disabled={!canEditFields}
                onChange={e=>f('notes',e.target.value)}
                placeholder="Conditions de paiement, délai de production, mentions légales…"/>
            </div>
          </div>

          {/* Colonne latérale */}
          <div style={{ display:'flex', flexDirection:'column', gap:'var(--space-md)' }}>
            <div className="admin-card" style={{ padding:'var(--space-lg)' }}>
              <h3 style={{ fontSize:14, fontWeight:700, marginBottom:'var(--space-md)', color:'var(--text-secondary)' }}>PARAMÈTRES</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:'var(--space-sm)' }}>
                <FormField label="Statut">
                  <Select value={form.status} onChange={e=>f('status',e.target.value)} disabled={!canEditStatus}>
                    {Object.entries(statusLabels).map(([v,l])=><option key={v} value={v}>{l}</option>)}
                  </Select>
                </FormField>
                {isInvoice && (
                  <FormField label="Date d'échéance">
                    <Input type="date" value={form.due_date} disabled={!canEditFields} onChange={e=>f('due_date',e.target.value)}/>
                  </FormField>
                )}
                {!isInvoice && (
                  <FormField label="Valide jusqu'au">
                    <Input type="date" value={form.valid_until} disabled={!canEditFields} onChange={e=>f('valid_until',e.target.value)}/>
                  </FormField>
                )}
              </div>
            </div>

            {/* Actions rapides */}
            {!isNew && (
              <div className="admin-card" style={{ padding:'var(--space-lg)' }}>
                <h3 style={{ fontSize:14, fontWeight:700, marginBottom:'var(--space-md)', color:'var(--text-secondary)' }}>ACTIONS</h3>
                <div style={{ display:'flex', flexDirection:'column', gap:'var(--space-sm)' }}>
                  <Button variant="secondary" fullWidth loading={pdfLoading} onClick={handleOpenPdf}>👁 Aperçu PDF</Button>
                  <Button variant="secondary" fullWidth loading={pdfLoading} onClick={handleDownPdf}>⬇ Télécharger PDF</Button>
                  {!isInvoice && doc && !['converted','rejected','cancelled'].includes(doc.status) && (
                    <Button variant="primary" fullWidth onClick={() => setConvertConfirm(true)}>🧾 Convertir en facture</Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={convertConfirm}
        onClose={() => setConvertConfirm(false)}
        onConfirm={async () => {
          setConvertConfirm(false);
          try {
            const { data } = await quoteAPI.convert(id);
            toast.success('Facture ' + data.data.reference + ' créée ✓');
            navigate('/admin/invoices/' + data.data.id + '/edit');
          } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
        }}
        title="Convertir en facture"
        message={`Convertir le devis ${doc?.reference} en facture ? Un numéro de facture sera automatiquement attribué. Cette action ne peut pas être annulée.`}
        confirmLabel="Convertir"
      />
    </div>
  );
};

/* ─── Pages exportées ─── */
export const InvoiceEditPage    = () => <DocumentEditPage type="invoice" />;
export const InvoiceNewPage     = () => <DocumentEditPage type="invoice" forceNew />;
export const QuoteEditPage      = () => <DocumentEditPage type="quote"   />;
export const QuoteNewPage       = () => <DocumentEditPage type="quote"   forceNew />;

/* ─── Modal envoi email avec template ─── */
const SendEmailModal = ({ isOpen, onClose, docType, docData, templates }) => {
  const [tplId,        setTplId]      = useState('');
  const [vars,         setVars]       = useState({});
  const [sending,      setSending]    = useState(false);
  const [attachPdf,    setAttachPdf]  = useState(true);
  const [siteSettings, setSiteSettings] = useState({});

  // Charger les paramètres ARTIMARQ à l'ouverture
  useEffect(() => {
    if (!isOpen) return;
    settingsAPI.public().then(({ data }) => {
      const s = data.data || {};
      setSiteSettings({
        site_name:     s.site_name     || 'ARTIMARQ',
        site_email:    s.site_email    || '',
        site_phone:    s.site_phone    || '',
        site_address:  s.site_address  || '',
        legal_company: s.legal_company || s.site_name || 'ARTIMARQ',
        legal_siret:   s.legal_siret   || '',
        legal_rcs:     s.legal_rcs     || '',
      });
    }).catch(() => {});
  }, [isOpen]);

  const filtered = templates.filter(t => t.category === docType || t.category === 'general');
  const selected = filtered.find(t => String(t.id) === String(tplId));

  const parseSnap = (d) => {
    if (!d) return {};
    const s = d.client_snapshot;
    return typeof s === 'string' ? (JSON.parse(s || '{}') || {}) : (s || {});
  };

  useEffect(() => {
    if (!selected || !docData) return;
    const snap = parseSnap(docData);
    const auto = {
      // Variables client / document
      client_name:    [snap.first_name, snap.last_name].filter(Boolean).join(' ') || snap.contact_name || '',
      company:        snap.company || docData.client_company || '',
      client_email:   snap.email   || docData.client_email   || '',
      quote_ref:      docData.reference || '',
      invoice_ref:    docData.reference || '',
      total_ttc:      parseFloat(docData.total_ttc   || 0).toFixed(2) + ' €',
      total_ht:       parseFloat(docData.subtotal_ht || 0).toFixed(2) + ' €',
      tax_amount:     parseFloat(docData.tax_amount  || 0).toFixed(2) + ' €',
      balance_due:    parseFloat(docData.balance_due || 0).toFixed(2) + ' €',
      valid_until:    docData.valid_until ? new Date(docData.valid_until).toLocaleDateString('fr-FR') : '',
      due_date:       docData.due_date    ? new Date(docData.due_date).toLocaleDateString('fr-FR')    : '',
      payment_status: docData.status || '',
      // Variables ARTIMARQ — pré-remplies depuis les Paramètres
      ...siteSettings,
    };
    const prefilled = {};
    (selected.variables || []).forEach(v => { prefilled[v] = auto[v] ?? ''; });
    setVars(prefilled);
  }, [tplId, selected, docData, siteSettings]);

  useEffect(() => { if (!isOpen) { setTplId(''); setVars({}); setAttachPdf(true); } }, [isOpen]);

  const clientEmail = (() => {
    const snap = parseSnap(docData);
    return snap.email || docData?.client_email || '';
  })();

  const send = async () => {
    if (!tplId)        { toast.error('Choisissez un template'); return; }
    if (!clientEmail)  { toast.error('Email client introuvable sur ce document'); return; }
    setSending(true);
    try {
      const payload = { recipients: [clientEmail], variables: vars };
      if (attachPdf && docData?.id) {
        if (docType === 'facture') payload.attach_invoice_id = docData.id;
        else                       payload.attach_quote_id   = docData.id;
      }
      const res = await emailTemplateAPI.send(parseInt(tplId), payload);
      const d = res.data;
      if (d?.data?.warning) {
        toast.warning(d.data.warning, { autoClose: 8000 });
      } else {
        toast.success(d?.message || 'Email envoyé ✓');
      }
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur envoi'); }
    finally { setSending(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="📤 Envoyer par email" size="md"
      footer={<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <Button variant="ghost" onClick={onClose}>Annuler</Button>
        <Button variant="primary" loading={sending} onClick={send}>Envoyer</Button>
      </div>}
    >
      <div style={{display:'flex',flexDirection:'column',gap:'var(--space-md)'}}>
        <div style={{padding:'10px 14px',background:'var(--bg-page)',borderRadius:'var(--radius-sm)',fontSize:13}}>
          <strong>Destinataire :</strong>{' '}
          {clientEmail || <em style={{color:'var(--error)'}}>Aucun email client sur ce document</em>}
        </div>
        <FormField label="Template" required>
          <select className="form-input form-select" value={tplId} onChange={e => setTplId(e.target.value)}>
            <option value="">-- Choisir un template --</option>
            {filtered.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </FormField>
        {selected && (selected.variables || []).length > 0 && (
          <div>
            <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:8}}>
              Variables du template
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {(selected.variables || []).map(v => (
                <FormField key={v} label={<code style={{fontSize:12,fontFamily:'monospace'}}>{`{{${v}}}`}</code>}>
                  <Input value={vars[v] || ''} onChange={e => setVars(p => ({...p,[v]:e.target.value}))} placeholder={`Valeur pour ${v}…`} />
                </FormField>
              ))}
            </div>
          </div>
        )}
        {/* ── Pièce jointe PDF ── */}
        {docData?.id && (
          <label style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:attachPdf?'rgba(196,30,58,.05)':'var(--bg-page)',border:`1px solid ${attachPdf?'rgba(196,30,58,.2)':'var(--border-light)'}`,borderRadius:'var(--radius-sm)',cursor:'pointer',userSelect:'none',fontSize:13,transition:'all .15s'}}>
            <input type="checkbox" checked={attachPdf} onChange={e => setAttachPdf(e.target.checked)} style={{width:16,height:16,accentColor:'var(--brand-accent)',cursor:'pointer'}} />
            <span>
              <strong>Joindre le {docType} en PDF</strong>
              <span style={{display:'block',fontSize:11,color:'var(--text-muted)',marginTop:2}}>
                {docData.reference} — le document sera joint en pièce jointe à l'email
              </span>
            </span>
          </label>
        )}
      </div>
    </Modal>
  );
};

/* ─── Modal signature électronique devis ─── */
const SignatureModal = ({ isOpen, onClose, quote }) => {
  const snap = quote ? (typeof quote.client_snapshot === 'string'
    ? (JSON.parse(quote.client_snapshot || '{}') || {})
    : (quote.client_snapshot || {})) : {};
  const [email,   setEmail]   = useState('');
  const [sending, setSending] = useState(false);
  const [result,  setResult]  = useState(null);

  useEffect(() => {
    if (isOpen) { setEmail(snap.email || quote?.client_email || ''); setResult(null); }
  }, [isOpen, quote?.client_email, snap.email]);

  const send = async () => {
    if (!email || !email.includes('@')) { toast.error('Email invalide'); return; }
    setSending(true);
    try {
      const { data } = await signatureAPI.createRequest(quote.id, {
        client_email: email,
        client_name:  snap.contact_name || snap.company || 'Client',
        base_url:     window.location.origin,
      });
      setResult(data.data);
      toast.success('Demande de signature envoyée ✓');
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSending(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="✍️ Signature électronique" size="md"
      footer={result
        ? <Button variant="primary" onClick={onClose}>Fermer</Button>
        : <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <Button variant="ghost" onClick={onClose}>Annuler</Button>
            <Button variant="primary" loading={sending} onClick={send}>✍️ Envoyer le lien</Button>
          </div>
      }
    >
      {result ? (
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={{padding:14,background:'var(--success-bg)',border:'1px solid var(--success)',borderRadius:'var(--radius-sm)',fontSize:13}}>
            ✅ Lien envoyé à <strong>{email}</strong>
          </div>
          <FormField label="Lien de signature (à copier si besoin)">
            <div style={{display:'flex',gap:8}}>
              <Input value={result.sign_url || ''} readOnly style={{fontFamily:'monospace',fontSize:11}} />
              <Button variant="secondary" size="sm" onClick={() => { navigator.clipboard.writeText(result.sign_url); toast.success('Copié'); }}>📋</Button>
            </div>
          </FormField>
          <div style={{fontSize:12,color:'var(--text-muted)'}}>
            Expire le {result.expires_at ? new Date(result.expires_at).toLocaleDateString('fr-FR') : '—'}
          </div>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:'var(--space-md)'}}>
          <div style={{padding:'10px 14px',background:'var(--bg-page)',borderRadius:'var(--radius-sm)',fontSize:13}}>
            Devis <strong>{quote?.reference}</strong> — {parseFloat(quote?.total_ttc||0).toLocaleString('fr-FR',{minimumFractionDigits:2})} € TTC
          </div>
          <FormField label="Email du client" required hint="Le client recevra un lien sécurisé valable 7 jours">
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="client@exemple.fr" />
          </FormField>
          <div style={{padding:10,background:'rgba(59,130,246,.06)',border:'1px solid rgba(59,130,246,.2)',borderRadius:'var(--radius-sm)',fontSize:12,color:'var(--text-secondary)'}}>
            🔒 Signature horodatée — IP enregistrée — preuve cryptographique SHA-256 générée automatiquement.
          </div>
        </div>
      )}
    </Modal>
  );
};

/* ═══════════════════════════════════════════════════════════
   LISTE FACTURES
   ═══════════════════════════════════════════════════════════ */
export const AdminInvoicesFullPage = () => {
  const navigate = useNavigate();
  const [invoices,   setInvoices]   = useState([]);
  const [meta,       setMeta]       = useState(null);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [status,     setStatus]     = useState('');
  const [confirm,    setConfirm]    = useState(null);
  const [payModal,   setPayModal]   = useState(null);
  const [sendModal,  setSendModal]  = useState(null); // invoice object
  const [templates,  setTemplates]  = useState([]);
  const [payForm,    setPayForm]    = useState({ amount:'', method:'bank_transfer', reference:'', paid_at: new Date().toISOString().split('T')[0], notes:'' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const { loading: pdfLoading, openPdf, downloadPdf } = usePdf();

  useEffect(() => {
    emailTemplateAPI.list().then(({data}) => setTemplates(data.data || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    invoiceAPI.list({ page, per_page:16, ...(search?{search}:{}), ...(status?{status}:{}) })
      .then(({data}) => { setInvoices(data.data||[]); setMeta(data.meta); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try { await invoiceAPI.delete(id); toast.success('Supprimée'); load(); } catch { toast.error('Erreur'); }
    setConfirm(null);
  };

  const addPayment = async () => {
    if (!payForm.amount || parseFloat(payForm.amount)<=0) { toast.error('Montant invalide'); return; }
    try { await invoiceAPI.addPayment(payModal, payForm); toast.success('Règlement enregistré ✓'); setPayModal(null); load(); }
    catch { toast.error('Erreur'); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isAllSelected = (list) => list.length > 0 && list.every(i => selectedIds.has(i.id));
  const toggleAll = (list) => setSelectedIds(isAllSelected(list) ? new Set() : new Set(list.map(i => i.id)));
  const bulkDelete = async () => {
    let ok = 0;
    for (const id of selectedIds) { try { await invoiceAPI.delete(id); ok++; } catch {} }
    toast.success(`${ok} élément(s) supprimé(s)`);
    setSelectedIds(new Set()); setBulkConfirm(false); load();
  };

  // Facture modifiable si aucun règlement enregistré
  const invEditable  = (inv) => !(inv.payments?.length > 0);
  const statusLabels = { draft:'Brouillon', sent:'Envoyé', paid:'Payé', partial:'Partiel', overdue:'En retard', cancelled:'Annulé' };
  const statusBadge  = { draft:'badge-neutral', sent:'badge-info', paid:'badge-success', partial:'badge-warning', overdue:'badge-error', cancelled:'badge-neutral' };
  const methods = { bank_transfer:'Virement', check:'Chèque', cash:'Espèces', card:'Carte', other:'Autre' };

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1 }}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Factures</h1><p className="admin-page-subtitle">{meta?.total||0} factures</p></div>
        <Button variant="primary" onClick={() => navigate('/admin/invoices/new')}>+ Nouvelle facture</Button>
      </div>
      <div className="admin-page-content">
        <div className="admin-card">
          <div className="admin-card-header">
            <div className="search-bar" style={{ maxWidth:280 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input className="form-input" placeholder="Référence, client…" value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} style={{paddingLeft:36}}/>
            </div>
            <select className="filter-select" value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
              <option value="">Tous les statuts</option>
              {Object.entries(statusLabels).map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {loading ? <div className="page-loader"><div className="spinner"/></div> :
            !invoices.length ? (
              <div style={{padding:'var(--space-xl)',textAlign:'center',color:'var(--text-muted)'}}>
                <div style={{fontSize:40,marginBottom:12}}>🧾</div>
                <p>Aucune facture. <button onClick={()=>navigate('/admin/invoices/new')} style={{color:'var(--brand-accent)',background:'none',border:'none',cursor:'pointer',fontWeight:600}}>Créer la première</button></p>
              </div>
            ) : (
              <>
                {selectedIds.size > 0 && (
                  <div style={{display:'flex',alignItems:'center',gap:12,padding:'8px 16px',background:'rgba(230,57,70,.06)',borderRadius:'var(--radius-sm)',border:'1px solid rgba(230,57,70,.2)',marginBottom:'var(--space-sm)'}}>
                    <span style={{fontSize:13,fontWeight:600,color:'var(--brand-accent)'}}>{selectedIds.size} sélectionné(s)</span>
                    <button style={{marginLeft:'auto',fontSize:12,color:'var(--text-muted)',background:'none',border:'none',cursor:'pointer'}} onClick={()=>setSelectedIds(new Set())}>Tout désélectionner</button>
                    <Button size="sm" variant="ghost" onClick={()=>setBulkConfirm(true)} style={{color:'var(--error)'}}>🗑 Supprimer ({selectedIds.size})</Button>
                  </div>
                )}
                <table className="data-table">
                  <thead><tr><th style={{width:36}}><input type="checkbox" checked={isAllSelected(invoices)} onChange={()=>toggleAll(invoices)} /></th><th>Référence</th><th>Client</th><th>Total TTC</th><th>Solde dû</th><th>Échéance</th><th>Statut</th><th>Actions</th></tr></thead>
                  <tbody>
                    {invoices.map(inv=>(
                      <tr key={inv.id}>
                        <td><input type="checkbox" checked={selectedIds.has(inv.id)} onChange={()=>toggleSelect(inv.id)} /></td>
                        <td><strong style={{fontFamily:'monospace',fontSize:13}}>{inv.reference}</strong></td>
                      <td style={{fontSize:13}}>{(typeof inv.client_snapshot==='object'?inv.client_snapshot?.company:null)||inv.client_company||'—'}</td>
                      <td style={{fontWeight:700}}>{parseFloat(inv.total_ttc||0).toLocaleString('fr-FR',{minimumFractionDigits:2})} €</td>
                      <td style={{color:parseFloat(inv.balance_due||0)>0?'var(--error)':'var(--success)',fontWeight:600}}>{parseFloat(inv.balance_due||0).toLocaleString('fr-FR',{minimumFractionDigits:2})} €</td>
                      <td style={{fontSize:12,color:'var(--text-muted)'}}>{inv.due_date?new Date(inv.due_date).toLocaleDateString('fr-FR'):'—'}</td>
                      <td><span className={`badge ${statusBadge[inv.status]||'badge-neutral'}`}>{statusLabels[inv.status]||inv.status}</span></td>
                      <td>
                        <div style={{display:'flex',gap:4}}>
                          <Button size="sm" variant="secondary" onClick={()=>navigate(`/admin/invoices/${inv.id}/edit`)}>
                            {invEditable(inv)?'✏️ Modifier':'👁 Voir'}
                          </Button>
                          <Button size="sm" variant="ghost" loading={pdfLoading} onClick={()=>openPdf(()=>invoiceAPI.fetchPdf(inv.id),'facture-'+inv.reference)} title="Aperçu PDF">📄</Button>
                          <Button size="sm" variant="ghost" loading={pdfLoading} onClick={()=>downloadPdf(()=>invoiceAPI.downloadPdf(inv.id),'facture-'+inv.reference)} title="Télécharger">⬇</Button>
                          <Button size="sm" variant="ghost" onClick={()=>setSendModal(inv)} title="Envoyer par email"><SendIcon /></Button>
                          {!['paid','cancelled'].includes(inv.status)&&(
                            <Button size="sm" variant="ghost" onClick={()=>setPayModal(inv.id)} title="Règlement">💳</Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={()=>setConfirm(inv.id)}>🗑</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </>
            )
          }
          {meta && (
            <div style={{padding:'var(--space-md) var(--space-lg)',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,color:'var(--text-muted)'}}>
              <span>{meta.total} facture(s) — page {meta.page}/{meta.total_pages}</span>
              <div style={{display:'flex',gap:8}}>
                {meta.has_prev&&<Button size="sm" variant="secondary" onClick={()=>setPage(p=>p-1)}>← Préc.</Button>}
                {meta.has_next&&<Button size="sm" variant="secondary" onClick={()=>setPage(p=>p+1)}>Suiv. →</Button>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal règlement */}
      <Modal isOpen={!!payModal} onClose={()=>setPayModal(null)} title="Enregistrer un règlement"
        footer={<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}><Button variant="ghost" onClick={()=>setPayModal(null)}>Annuler</Button><Button variant="primary" onClick={addPayment}>Enregistrer</Button></div>}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
          <FormField label="Montant (€)" required><Input type="number" step="0.01" value={payForm.amount} onChange={e=>setPayForm(p=>({...p,amount:e.target.value}))}/></FormField>
          <FormField label="Date"><Input type="date" value={payForm.paid_at} onChange={e=>setPayForm(p=>({...p,paid_at:e.target.value}))}/></FormField>
          <FormField label="Mode">
            <Select value={payForm.method} onChange={e=>setPayForm(p=>({...p,method:e.target.value}))}>
              {Object.entries(methods).map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </Select>
          </FormField>
          <FormField label="Référence"><Input value={payForm.reference} onChange={e=>setPayForm(p=>({...p,reference:e.target.value}))} placeholder="N° virement…"/></FormField>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!confirm} onClose={()=>setConfirm(null)} onConfirm={()=>del(confirm)} title="Supprimer la facture" message="Action irréversible." confirmLabel="Supprimer" danger/>
      <SendEmailModal isOpen={!!sendModal} onClose={()=>setSendModal(null)} docType="facture" docData={sendModal} templates={templates} />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   LISTE DEVIS
   ═══════════════════════════════════════════════════════════ */
export const AdminQuotesFullPage = () => {
  const navigate = useNavigate();
  const [quotes,     setQuotes]    = useState([]);
  const [meta,       setMeta]      = useState(null);
  const [page,       setPage]      = useState(1);
  const [loading,    setLoading]   = useState(true);
  const [search,     setSearch]    = useState('');
  const [status,     setStatus]    = useState('');
  const [confirm,        setConfirm]        = useState(null);
  const [convertConfirm, setConvertConfirm] = useState(null);
  const [sendModal,      setSendModal]      = useState(null); // quote object
  const [sigModal,       setSigModal]       = useState(null); // quote object
  const [templates,      setTemplates]      = useState([]);
  const { loading: pdfLoading, openPdf, downloadPdf } = usePdf();

  useEffect(() => {
    emailTemplateAPI.list().then(({data}) => setTemplates(data.data || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    quoteAPI.list({ page, per_page:16, ...(search?{search}:{}), ...(status?{status}:{}) })
      .then(({data})=>{setQuotes(data.data||[]);setMeta(data.meta);})
      .catch(()=>{}).finally(()=>setLoading(false));
  },[page,search,status]);

  useEffect(()=>{load();},[load]);

  const del = async(id)=>{ try{await quoteAPI.delete(id);toast.success('Supprimé');load();}catch{toast.error('Erreur');} setConfirm(null); };
  const convert = async(id)=>{ try{const{data}=await quoteAPI.convert(id);toast.success('Facture '+data.data.reference+' créée ✓');load();}catch(err){toast.error(err.response?.data?.message||'Erreur');} setConvertConfirm(null); };

  const statusLabels={pending:'En attente',sent:'Envoyé',accepted:'Accepté',rejected:'Refusé',expired:'Expiré',converted:'Converti',draft:'Brouillon'};
  const statusBadge ={pending:'badge-warning',sent:'badge-info',accepted:'badge-success',rejected:'badge-error',expired:'badge-neutral',converted:'badge-accent',draft:'badge-neutral'};

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1}}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Devis</h1><p className="admin-page-subtitle">{meta?.total||0} devis</p></div>
        <Button variant="primary" onClick={()=>navigate('/admin/quotes/new')}>+ Nouveau devis</Button>
      </div>
      <div className="admin-page-content">
        <div className="admin-card">
          <div className="admin-card-header">
            <div className="search-bar" style={{maxWidth:280}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input className="form-input" placeholder="Référence, client…" value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} style={{paddingLeft:36}}/>
            </div>
            <select className="filter-select" value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
              <option value="">Tous les statuts</option>
              {Object.entries(statusLabels).map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {loading?<div className="page-loader"><div className="spinner"/></div>:
            !quotes.length?(
              <div style={{padding:'var(--space-xl)',textAlign:'center',color:'var(--text-muted)'}}>
                <div style={{fontSize:40,marginBottom:12}}>📋</div>
                <p>Aucun devis. <button onClick={()=>navigate('/admin/quotes/new')} style={{color:'var(--brand-accent)',background:'none',border:'none',cursor:'pointer',fontWeight:600}}>Créer le premier</button></p>
              </div>
            ):(
              <table className="data-table">
                <thead><tr><th>Référence</th><th>Client</th><th>Total TTC</th><th>Statut</th><th>Valide jusqu'au</th><th>Date</th><th>Actions</th></tr></thead>
                <tbody>
                  {quotes.map(q=>(
                    <tr key={q.id}>
                      <td><code style={{fontSize:12,background:'var(--bg-page)',padding:'2px 6px',borderRadius:4}}>{q.reference}</code></td>
                      <td style={{fontWeight:600,fontSize:13}}>
                        {(typeof q.client_snapshot==='object'?q.client_snapshot?.company:null)||q.client_company||'—'}
                      </td>
                      <td style={{fontWeight:700,color:'var(--brand-accent)'}}>{parseFloat(q.total_ttc||0).toLocaleString('fr-FR',{minimumFractionDigits:2})} €</td>
                      <td><span className={`badge ${statusBadge[q.status]||'badge-neutral'}`}>{statusLabels[q.status]||q.status}</span></td>
                      <td style={{fontSize:12,color:'var(--text-muted)'}}>{q.valid_until?new Date(q.valid_until).toLocaleDateString('fr-FR'):'—'}</td>
                      <td style={{fontSize:12,color:'var(--text-muted)'}}>{new Date(q.created_at).toLocaleDateString('fr-FR')}</td>
                      <td>
                        <div style={{display:'flex',gap:4}}>
                          <Button size="sm" variant="secondary" onClick={()=>navigate(`/admin/quotes/${q.id}/edit`)}>
                            ✏️ Modifier
                          </Button>
                          <Button size="sm" variant="ghost" loading={pdfLoading} onClick={()=>openPdf(()=>quoteAPI.fetchPdf(q.id),'devis-'+q.reference)} title="Aperçu">📄</Button>
                          <Button size="sm" variant="ghost" loading={pdfLoading} onClick={()=>downloadPdf(()=>quoteAPI.downloadPdf(q.id),'devis-'+q.reference)} title="Télécharger">⬇</Button>
                          <Button size="sm" variant="ghost" onClick={()=>setSendModal(q)} title="Envoyer par email"><SendIcon /></Button>
                          {!['converted','rejected','cancelled','accepted'].includes(q.status)&&<Button size="sm" variant="ghost" onClick={()=>setSigModal(q)} title="Demande de signature">✍️</Button>}
                          {!['converted','rejected','cancelled'].includes(q.status)&&<Button size="sm" variant="primary" onClick={()=>setConvertConfirm(q)} title="Convertir en facture">🧾</Button>}
                          <Button size="sm" variant="ghost" onClick={()=>setConfirm(q.id)}>🗑</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
          {meta&&(
            <div style={{padding:'var(--space-md) var(--space-lg)',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,color:'var(--text-muted)'}}>
              <span>{meta.total} devis — page {meta.page}/{meta.total_pages}</span>
              <div style={{display:'flex',gap:8}}>
                {meta.has_prev&&<Button size="sm" variant="secondary" onClick={()=>setPage(p=>p-1)}>← Préc.</Button>}
                {meta.has_next&&<Button size="sm" variant="secondary" onClick={()=>setPage(p=>p+1)}>Suiv. →</Button>}
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog isOpen={!!confirm} onClose={()=>setConfirm(null)} onConfirm={()=>del(confirm)} title="Supprimer le devis" message="Action irréversible." confirmLabel="Supprimer" danger/>
      <ConfirmDialog
        isOpen={!!convertConfirm}
        onClose={()=>setConvertConfirm(null)}
        onConfirm={()=>convert(convertConfirm?.id)}
        title="Convertir en facture"
        message={`Convertir le devis ${convertConfirm?.reference} en facture ? Un numéro de facture sera automatiquement attribué. Cette action ne peut pas être annulée.`}
        confirmLabel="Convertir"
      />
      <SendEmailModal isOpen={!!sendModal} onClose={()=>setSendModal(null)} docType="devis" docData={sendModal} templates={templates} />
      <SignatureModal isOpen={!!sigModal}  onClose={()=>setSigModal(null)}  quote={sigModal} />
    </div>
  );
};